// Brazilian postal code (CEP) lookup: ViaCEP first, BrasilAPI as fallback.
// Both APIs are called directly from the browser (CORS is open on both).

export const VIACEP_URL = (cep) => `https://viacep.com.br/ws/${cep}/json/`;
export const BRASILAPI_URL = (cep) => `https://brasilapi.com.br/api/cep/v2/${cep}`;
export const DEFAULT_CEP_TIMEOUT_MS = 3000;

export const CEP_LOOKUP_STATUS = Object.freeze({
  FOUND: "found",
  NOT_FOUND: "not_found",
  INVALID: "invalid",
  UNAVAILABLE: "unavailable",
});

const cepCache = new Map();

export const clearCepCache = () => cepCache.clear();

export const normalizeCepDigits = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 8);

const isTrueFlag = (value) => value === true || value === "true";

const createAbortError = () => {
  const error = new Error("The CEP lookup was aborted");
  error.name = "AbortError";
  return error;
};

// Fetches with a per-request timeout while still honouring the caller's signal.
const fetchWithTimeout = async (url, { fetchImpl, signal, timeoutMs }) => {
  if (signal?.aborted) throw createAbortError();

  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort);
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetchImpl(url, { signal: controller.signal, headers: { Accept: "application/json" } });
  } catch (error) {
    if (signal?.aborted) throw createAbortError();
    if (timedOut) {
      const timeoutError = new Error("The CEP lookup timed out");
      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
};

const toText = (value) => (typeof value === "string" ? value.trim() : "");

const fromViaCep = (cep, data) => ({
  postalCode: cep,
  street: toText(data.logradouro),
  neighborhood: toText(data.bairro),
  city: toText(data.localidade),
  state: toText(data.uf).toUpperCase(),
});

const fromBrasilApi = (cep, data) => ({
  postalCode: cep,
  street: toText(data.street),
  neighborhood: toText(data.neighborhood),
  city: toText(data.city),
  state: toText(data.state).toUpperCase(),
});

const lookupViaCep = async (cep, options) => {
  const response = await fetchWithTimeout(VIACEP_URL(cep), options);
  if (response.status === 400) return { status: CEP_LOOKUP_STATUS.INVALID };
  if (!response.ok) {
    throw new Error(`ViaCEP responded with ${response.status}`);
  }
  const data = await response.json();
  if (!data || isTrueFlag(data.erro)) return { status: CEP_LOOKUP_STATUS.NOT_FOUND };
  return { status: CEP_LOOKUP_STATUS.FOUND, source: "viacep", address: fromViaCep(cep, data) };
};

const lookupBrasilApi = async (cep, options) => {
  const response = await fetchWithTimeout(BRASILAPI_URL(cep), options);
  if (response.status === 404) return { status: CEP_LOOKUP_STATUS.NOT_FOUND };
  if (response.status === 400) return { status: CEP_LOOKUP_STATUS.INVALID };
  if (!response.ok) {
    throw new Error(`BrasilAPI responded with ${response.status}`);
  }
  const data = await response.json();
  return { status: CEP_LOOKUP_STATUS.FOUND, source: "brasilapi", address: fromBrasilApi(cep, data) };
};

/**
 * Resolves a CEP to an address.
 * Resolves with `{ status, address?, source? }`; rejects only with an AbortError
 * when the caller's `signal` is aborted.
 */
export const lookupCep = async (
  value,
  {
    signal,
    fetchImpl = (...args) => fetch(...args),
    timeoutMs = DEFAULT_CEP_TIMEOUT_MS,
    cache = cepCache,
  } = {},
) => {
  const cep = normalizeCepDigits(value);
  if (cep.length !== 8) return { status: CEP_LOOKUP_STATUS.INVALID };
  if (cache?.has(cep)) return cache.get(cep);

  const options = { fetchImpl, signal, timeoutMs };
  let result;
  try {
    result = await lookupViaCep(cep, options);
  } catch (viaCepError) {
    if (viaCepError?.name === "AbortError") throw viaCepError;
    try {
      result = await lookupBrasilApi(cep, options);
    } catch (brasilApiError) {
      if (brasilApiError?.name === "AbortError") throw brasilApiError;
      // Transient failures are not cached so a retry can still succeed.
      return { status: CEP_LOOKUP_STATUS.UNAVAILABLE };
    }
  }

  cache?.set(cep, result);
  return result;
};
